import { useState, useRef, useCallback } from 'react';

interface StyleAspect {
  id: string;
  label: string;
  icon: string;
}

const STYLE_ASPECTS: StyleAspect[] = [
  { id: 'color', label: 'Color Palette', icon: 'ri-palette-line' },
  { id: 'lighting', label: 'Lighting', icon: 'ri-sun-foggy-line' },
  { id: 'texture', label: 'Texture', icon: 'ri-fingerprint-line' },
  { id: 'composition', label: 'Composition', icon: 'ri-layout-5-line' },
  { id: 'mood', label: 'Mood & Tone', icon: 'ri-emotion-line' },
  { id: 'brush', label: 'Brush / Line Work', icon: 'ri-brush-2-line' },
];

const PRESET_STYLES = [
  { id: 'vangogh', label: 'Van Gogh', desc: 'Swirling impasto strokes, vibrant yellows and blues', icon: '🌻' },
  { id: 'monet', label: 'Monet', desc: 'Soft impressionist dabs, hazy pastel light', icon: '🌸' },
  { id: 'cyberpunk', label: 'Cyberpunk', desc: 'Neon-drenched darkness, rain-slick streets', icon: '⚡' },
  { id: 'inkwash', label: 'Ink Wash', desc: 'East Asian sumi-e ink, minimal brushwork', icon: '🖌️' },
  { id: 'pastel', label: 'Pastel Dream', desc: 'Soft muted tones, dreamy diffused glow', icon: '🌈' },
  { id: 'ukiyo', label: 'Ukiyo-e', desc: 'Japanese woodblock print, bold flat colors', icon: '🗻' },
  { id: 'bauhaus', label: 'Bauhaus', desc: 'Geometric shapes, primary colors, bold lines', icon: '◻' },
  { id: 'noir', label: 'Film Noir', desc: 'High contrast black & white, dramatic shadows', icon: '🎬' },
];

const STRENGTH_LABELS = ['Light', 'Subtle', 'Medium', 'Strong', 'Maximum'];

const PRESET_PROMPT_MAP: Record<string, string> = {
  vangogh: 'painted in the style of Van Gogh, swirling expressive brushstrokes, thick impasto texture, vibrant yellows and deep blues, post-impressionist',
  monet: 'in the style of Claude Monet, soft impressionist brushwork, hazy pastel light, blurred reflections, plein air painting technique',
  cyberpunk: 'cyberpunk aesthetic, neon-lit darkness, rain-slick reflective streets, high-contrast glowing accents, futuristic dystopian atmosphere',
  inkwash: 'sumi-e ink wash painting, minimal East Asian brushwork, flowing ink gradients, negative space composition, traditional calligraphy influence',
  pastel: 'soft pastel color palette, dreamy diffused lighting, muted romantic tones, delicate airy atmosphere, fine art pastel illustration',
  ukiyo: 'ukiyo-e woodblock print style, bold flat color areas, strong outlines, traditional Japanese aesthetic, decorative patterns',
  bauhaus: 'Bauhaus design style, geometric abstraction, primary colors, bold lines, modernist composition, functional aesthetics',
  noir: 'film noir style, dramatic black and white photography, extreme chiaroscuro lighting, deep shadows, high contrast, cinematic tension',
};

export interface StyleTransferConfig {
  type: 'upload' | 'preset';
  presetId?: string;
  imageDataUrl?: string;
  imageName?: string;
  strength: number;
  aspects: string[];
  promptAddition: string;
}

interface StyleTransferPanelProps {
  darkMode: boolean;
  onConfigChange: (config: StyleTransferConfig | null) => void;
  activeConfig: StyleTransferConfig | null;
}

export default function StyleTransferPanel({ darkMode, onConfigChange, activeConfig }: StyleTransferPanelProps) {
  const [expanded, setExpanded] = useState(false);
  const [uploadedImage, setUploadedImage] = useState<{ dataUrl: string; name: string } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [strength, setStrength] = useState(3);
  const [selectedAspects, setSelectedAspects] = useState<string[]>(['color', 'lighting', 'mood']);
  const [selectedPreset, setSelectedPreset] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'upload' | 'preset'>('upload');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const buildPromptAddition = useCallback(
    (type: 'upload' | 'preset', presetId: string | null, aspects: string[], str: number): string => {
      const strengthDesc = ['lightly inspired by', 'subtly influenced by', 'styled after', 'strongly styled like', 'completely transformed to match'][str - 1];
      const aspectLabels = aspects.map(a => STYLE_ASPECTS.find(s => s.id === a)?.label ?? a).join(', ');
      if (type === 'preset' && presetId && PRESET_PROMPT_MAP[presetId]) {
        return `${strengthDesc} reference style: ${PRESET_PROMPT_MAP[presetId]}, transferring ${aspectLabels}`;
      }
      return `${strengthDesc} the uploaded reference image style, matching ${aspectLabels} from the reference`;
    },
    []
  );

  const handleFile = (file: File) => {
    setUploadError('');
    if (!file.type.startsWith('image/')) { setUploadError('Only image files are supported.'); return; }
    if (file.size > 10 * 1024 * 1024) { setUploadError('Image must be under 10MB.'); return; }
    const reader = new FileReader();
    reader.onload = e => {
      const dataUrl = e.target?.result as string;
      setUploadedImage({ dataUrl, name: file.name });
      setSelectedPreset(null);
      const addition = buildPromptAddition('upload', null, selectedAspects, strength);
      onConfigChange({ type: 'upload', imageDataUrl: dataUrl, imageName: file.name, strength, aspects: selectedAspects, promptAddition: addition });
    };
    reader.readAsDataURL(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault(); setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) handleFile(e.target.files[0]);
  };

  const toggleAspect = (id: string) => {
    const next = selectedAspects.includes(id)
      ? selectedAspects.filter(a => a !== id)
      : [...selectedAspects, id];
    setSelectedAspects(next);
    if (activeConfig) {
      const addition = buildPromptAddition(activeConfig.type, selectedPreset, next, strength);
      onConfigChange({ ...activeConfig, aspects: next, promptAddition: addition });
    }
  };

  const handleStrengthChange = (s: number) => {
    setStrength(s);
    if (activeConfig) {
      const addition = buildPromptAddition(activeConfig.type, selectedPreset, selectedAspects, s);
      onConfigChange({ ...activeConfig, strength: s, promptAddition: addition });
    }
  };

  const selectPreset = (id: string) => {
    setSelectedPreset(id === selectedPreset ? null : id);
    setUploadedImage(null);
    if (id === selectedPreset) { onConfigChange(null); return; }
    const addition = buildPromptAddition('preset', id, selectedAspects, strength);
    onConfigChange({ type: 'preset', presetId: id, strength, aspects: selectedAspects, promptAddition: addition });
  };

  const clearAll = () => {
    setUploadedImage(null);
    setSelectedPreset(null);
    setUploadError('');
    onConfigChange(null);
  };

  const isActive = !!activeConfig;

  return (
    <div className={`rounded-2xl border overflow-hidden transition-all ${
      isActive
        ? 'border-violet-400 dark:border-violet-600 ring-2 ring-violet-200 dark:ring-violet-800'
        : darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
    } ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>

      {/* Header */}
      <button
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center justify-between px-5 py-4 text-left"
      >
        <div className="flex items-center gap-3">
          <div className={`w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0 ${
            isActive
              ? 'bg-gradient-to-br from-violet-500 to-fuchsia-600'
              : 'bg-gradient-to-br from-violet-400/20 to-fuchsia-500/20'
          }`}>
            <i className={`ri-magic-line text-base ${isActive ? 'text-white' : 'text-violet-500'}`}></i>
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className={`text-sm font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Style Transfer</span>
              <span className="px-1.5 py-0.5 bg-gradient-to-r from-violet-500 to-fuchsia-600 text-white text-[9px] font-bold rounded-full uppercase tracking-wide">New</span>
              {isActive && (
                <span className="flex items-center gap-1 px-2 py-0.5 bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400 text-[10px] font-bold rounded-full">
                  <span className="w-1.5 h-1.5 bg-violet-500 rounded-full animate-pulse"></span>
                  Active
                </span>
              )}
            </div>
            <p className={`text-xs mt-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {isActive ? (activeConfig.type === 'preset' ? `Using: ${PRESET_STYLES.find(p => p.id === activeConfig.presetId)?.label}` : `Using: ${activeConfig.imageName}`) : 'Apply a reference image or preset style'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {isActive && (
            <button
              onClick={e => { e.stopPropagation(); clearAll(); }}
              className={`p-1.5 rounded-lg text-xs transition-colors ${darkMode ? 'text-gray-500 hover:text-red-400 hover:bg-red-900/20' : 'text-gray-400 hover:text-red-500 hover:bg-red-50'}`}
              title="Clear style transfer"
            >
              <i className="ri-close-line text-sm"></i>
            </button>
          )}
          <i className={`ri-arrow-${expanded ? 'up' : 'down'}-s-line text-lg ${darkMode ? 'text-gray-400' : 'text-gray-400'}`}></i>
        </div>
      </button>

      {/* Body */}
      {expanded && (
        <div className={`border-t px-5 pb-5 pt-4 space-y-5 ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}>

          {/* Tab switcher */}
          <div className={`flex p-1 rounded-xl gap-1 ${darkMode ? 'bg-gray-900' : 'bg-gray-100'}`}>
            {(['upload', 'preset'] as const).map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`flex-1 py-2 rounded-lg text-xs font-semibold transition-all capitalize ${
                  activeTab === tab
                    ? 'bg-gradient-to-r from-violet-500 to-fuchsia-600 text-white shadow'
                    : darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <i className={`${tab === 'upload' ? 'ri-upload-2-line' : 'ri-magic-line'} mr-1.5`}></i>
                {tab === 'upload' ? 'Upload Reference' : 'Preset Styles'}
              </button>
            ))}
          </div>

          {/* Upload tab */}
          {activeTab === 'upload' && (
            <div className="space-y-3">
              {uploadedImage ? (
                <div className={`rounded-xl overflow-hidden border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                  <div className="relative">
                    <img src={uploadedImage.dataUrl} alt="Reference" className="w-full h-40 object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                    <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
                      <div>
                        <p className="text-white text-xs font-semibold truncate max-w-[160px]">{uploadedImage.name}</p>
                        <p className="text-white/70 text-[10px]">Reference image active</p>
                      </div>
                      <div className="flex gap-1.5">
                        <button
                          onClick={() => fileInputRef.current?.click()}
                          className="px-2.5 py-1.5 bg-white/20 hover:bg-white/30 text-white text-xs font-medium rounded-lg backdrop-blur-sm transition-colors"
                        >
                          <i className="ri-refresh-line mr-1"></i>Change
                        </button>
                        <button
                          onClick={() => { setUploadedImage(null); onConfigChange(null); }}
                          className="px-2.5 py-1.5 bg-red-500/80 hover:bg-red-500 text-white text-xs font-medium rounded-lg backdrop-blur-sm transition-colors"
                        >
                          <i className="ri-delete-bin-line"></i>
                        </button>
                      </div>
                    </div>
                    {/* Active indicator */}
                    <div className="absolute top-2 right-2 flex items-center gap-1 px-2 py-1 bg-violet-600 text-white text-[10px] font-bold rounded-full">
                      <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></span>
                      Style Source
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all ${
                    isDragging
                      ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/20'
                      : darkMode ? 'border-gray-600 hover:border-violet-500 hover:bg-violet-900/10' : 'border-gray-300 hover:border-violet-400 hover:bg-violet-50'
                  }`}
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500/20 to-fuchsia-500/20 flex items-center justify-center mx-auto mb-3">
                    <i className="ri-image-add-line text-2xl text-violet-500"></i>
                  </div>
                  <p className={`text-sm font-semibold mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Drop your reference image here</p>
                  <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>JPG, PNG, WebP · max 10MB</p>
                  <p className={`text-xs mt-1 ${darkMode ? 'text-violet-500' : 'text-violet-600'}`}>or click to browse</p>
                </div>
              )}

              <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleInputChange} />

              {uploadError && (
                <p className="text-xs text-red-500 dark:text-red-400 flex items-center gap-1.5">
                  <i className="ri-error-warning-line"></i>{uploadError}
                </p>
              )}
            </div>
          )}

          {/* Preset tab */}
          {activeTab === 'preset' && (
            <div className="grid grid-cols-2 gap-2">
              {PRESET_STYLES.map(preset => (
                <button
                  key={preset.id}
                  onClick={() => selectPreset(preset.id)}
                  className={`text-left p-3 rounded-xl border-2 transition-all group ${
                    selectedPreset === preset.id
                      ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/20'
                      : darkMode ? 'border-gray-700 hover:border-violet-500 hover:bg-violet-900/10' : 'border-gray-200 hover:border-violet-300 hover:bg-violet-50'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-base leading-none">{preset.icon}</span>
                    <span className={`text-xs font-bold ${
                      selectedPreset === preset.id ? 'text-violet-600 dark:text-violet-400' : darkMode ? 'text-white' : 'text-gray-900'
                    }`}>{preset.label}</span>
                    {selectedPreset === preset.id && (
                      <i className="ri-check-line text-violet-500 ml-auto text-sm"></i>
                    )}
                  </div>
                  <p className={`text-[10px] leading-tight ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{preset.desc}</p>
                </button>
              ))}
            </div>
          )}

          {/* Style Aspects */}
          <div>
            <p className={`text-xs font-semibold mb-2.5 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              <i className="ri-focus-2-line mr-1.5 text-violet-500"></i>
              Transfer these aspects
            </p>
            <div className="flex flex-wrap gap-1.5">
              {STYLE_ASPECTS.map(aspect => (
                <button
                  key={aspect.id}
                  onClick={() => toggleAspect(aspect.id)}
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-medium border transition-all ${
                    selectedAspects.includes(aspect.id)
                      ? 'border-violet-500 bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400'
                      : darkMode ? 'border-gray-600 text-gray-400 hover:border-violet-500' : 'border-gray-200 text-gray-600 hover:border-violet-300'
                  }`}
                >
                  <i className={`${aspect.icon} text-xs`}></i>
                  {aspect.label}
                </button>
              ))}
            </div>
          </div>

          {/* Strength slider */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className={`text-xs font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <i className="ri-speed-line mr-1.5 text-violet-500"></i>
                Style Strength
              </p>
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                strength >= 4
                  ? 'bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400'
                  : darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
              }`}>{STRENGTH_LABELS[strength - 1]}</span>
            </div>
            <div className="flex items-center gap-1">
              {STRENGTH_LABELS.map((label, i) => (
                <button
                  key={label}
                  onClick={() => handleStrengthChange(i + 1)}
                  title={label}
                  className={`flex-1 h-7 rounded-lg text-[10px] font-bold transition-all ${
                    strength === i + 1
                      ? 'bg-gradient-to-r from-violet-500 to-fuchsia-600 text-white'
                      : darkMode ? 'bg-gray-700 text-gray-500 hover:bg-gray-600' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                  }`}
                >
                  {i + 1}
                </button>
              ))}
            </div>
            <div className="flex justify-between mt-1">
              <span className={`text-[10px] ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>Light</span>
              <span className={`text-[10px] ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>Maximum</span>
            </div>
          </div>

          {/* Prompt preview */}
          {isActive && (
            <div className={`rounded-xl p-3 border ${darkMode ? 'bg-gray-900 border-gray-700' : 'bg-violet-50 border-violet-200'}`}>
              <p className={`text-[10px] font-semibold uppercase tracking-wide mb-1.5 ${darkMode ? 'text-gray-500' : 'text-violet-500'}`}>
                <i className="ri-eye-line mr-1"></i>Style injection preview
              </p>
              <p className={`text-xs leading-relaxed italic ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                &ldquo;…{activeConfig.promptAddition}&rdquo;
              </p>
            </div>
          )}

          {/* CTA */}
          {!isActive && (activeTab === 'upload' && !uploadedImage) && activeTab === 'preset' && !selectedPreset && (
            <p className={`text-xs text-center ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>
              Upload an image or pick a preset to activate style transfer
            </p>
          )}
        </div>
      )}
    </div>
  );
}
